import { Effect } from "effect"
import { FetchHttpClient } from "effect/http"
import { call, errorTag } from "./api.ts"
import { createDocumentSaver, type DocumentSaver } from "./documentSaver.ts"

// Per-user JSON documents on the server (docs/design/0003): the workspace and
// each symbol's drawings. Callers decode what they load with their own Schemas.

export const loadDocument = async (key: string): Promise<string | null> => {
  const { json } = await call((client) => client.documents.get({ params: { key } }))
  return json
}

/** `keepalive`: the request outlives the page (a save as the tab closes). */
export const saveDocument = (key: string, json: string, { keepalive = false } = {}): Promise<unknown> =>
  call((client) => {
    const put = client.documents.put({ params: { key }, payload: { json } })
    return keepalive ? put.pipe(Effect.provideService(FetchHttpClient.RequestInit, { keepalive: true })) : put
  })

/** An expired session or a rejected document won't pass by trying again. */
const PERMANENT = new Set(["Unauthorized", "InvalidDocument"])

/**
 * A saver that keeps one request in flight per document and retries network and
 * server failures (documentSaver.ts); what's pending goes out as the page hides.
 */
export const documentSaver = (
  delayMs: number,
  onStatus?: (key: string, status: "saved" | "failed") => void
): DocumentSaver => {
  const saver = createDocumentSaver(delayMs, {
    put: (key, json, options) => saveDocument(key, json, options),
    retryable: (error) => !PERMANENT.has(errorTag(error)),
    ...(onStatus ? { onStatus } : {})
  })
  const flushOnHide = () => void saver.flush({ keepalive: true })
  window.addEventListener("pagehide", flushOnHide)
  document.addEventListener("visibilitychange", () => document.visibilityState === "hidden" && flushOnHide())
  return saver
}
